import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { notificationService } from '../api/services/notification.service'
import { realtimeService } from '../api/services/realtime.service'
import { toast } from '../lib/toast'
import type { AppNotification, UnreadNotificationSummary } from '../types/notification'
import { useAuth } from './AuthContext'

export type NotificationListener = (notification: AppNotification) => void

type NotificationContextValue = {
  unreadCount: number
  commentUnreadCount: (caseId: string) => number
  revision: number
  refreshUnreadSummary: () => Promise<void>
  /** Mark one notification read and drop the bell badge count immediately. */
  markNotificationRead: (id: string) => Promise<void>
  markCaseCommentsRead: (caseId: string) => Promise<void>
  /**
   * Subscribe to live `NotificationCreated` events (same Echo channel as the bell).
   * Returns an unsubscribe function. Safe to use from pages — does not leave the shared channel.
   */
  subscribe: (listener: NotificationListener) => () => void
}

const EMPTY_SUMMARY: UnreadNotificationSummary = {
  total: 0,
  comments_by_case: {},
}

const NotificationContext = createContext<NotificationContextValue | null>(null)

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { can, isAuthenticated, session } = useAuth()
  const [summary, setSummary] = useState<UnreadNotificationSummary>(EMPTY_SUMMARY)
  const [revision, setRevision] = useState(0)
  const canList = can('notifications-list')
  const listenersRef = useRef(new Set<NotificationListener>())

  const subscribe = useCallback((listener: NotificationListener) => {
    listenersRef.current.add(listener)

    return () => {
      listenersRef.current.delete(listener)
    }
  }, [])

  const refreshUnreadSummary = useCallback(async () => {
    if (!isAuthenticated || !canList) {
      setSummary(EMPTY_SUMMARY)
      return
    }

    try {
      const response = await notificationService.unreadSummary()
      setSummary({
        total: response.data.total,
        comments_by_case: response.data.comments_by_case ?? {},
      })
    } catch {
      setSummary(EMPTY_SUMMARY)
    }
  }, [canList, isAuthenticated])

  useEffect(() => {
    void refreshUnreadSummary()
  }, [refreshUnreadSummary])

  useEffect(() => {
    const userId = session?.user.id
    if (!userId || !canList || !realtimeService.isConfigured()) {
      return
    }

    const echo = realtimeService.connect()
    if (!echo) {
      return
    }

    const channel = echo.private(`user.${userId}`)
    const handler = (notification: AppNotification) => {
      setSummary((current) => {
        const commentsByCase = { ...current.comments_by_case }

        if (notification.type === 'comment_created' && notification.case_id) {
          commentsByCase[notification.case_id] =
            (commentsByCase[notification.case_id] ?? 0) + 1
        }

        return {
          total: current.total + 1,
          comments_by_case: commentsByCase,
        }
      })
      setRevision((current) => current + 1)
      toast.info(`${notification.title}: ${notification.body}`)

      listenersRef.current.forEach((listener) => {
        listener(notification)
      })
    }

    channel.listen('.NotificationCreated', handler)

    return () => {
      channel.stopListening('.NotificationCreated', handler)
      echo.leave(`user.${userId}`)
    }
  }, [canList, session?.user.id])

  const markNotificationRead = useCallback(async (id: string) => {
    if (!canList) {
      return
    }

    await notificationService.markRead(id)

    setSummary((current) => ({
      ...current,
      total: Math.max(0, current.total - 1),
    }))
    setRevision((current) => current + 1)
  }, [canList])

  const markCaseCommentsRead = useCallback(async (caseId: string) => {
    if (!canList) {
      return
    }

    const response = await notificationService.markCaseCommentsRead(caseId)
    const markedCount = response.data.marked_count

    setSummary((current) => {
      const commentsByCase = { ...current.comments_by_case }
      delete commentsByCase[caseId]

      return {
        total: Math.max(0, current.total - markedCount),
        comments_by_case: commentsByCase,
      }
    })
    setRevision((current) => current + 1)
  }, [canList])

  const value = useMemo<NotificationContextValue>(
    () => ({
      unreadCount: summary.total,
      commentUnreadCount: (caseId: string) => summary.comments_by_case[caseId] ?? 0,
      revision,
      refreshUnreadSummary,
      markNotificationRead,
      markCaseCommentsRead,
      subscribe,
    }),
    [markCaseCommentsRead, markNotificationRead, refreshUnreadSummary, revision, subscribe, summary],
  )

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationContext)

  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider')
  }

  return context
}
